import { MaxUint256 } from '@ethersproject/constants';
import { TransactionResponse } from '@ethersproject/providers';
import { useCallback, useMemo } from 'react';
import useActiveWeb3React from 'hooks/useActiveWeb3React';
import { logError } from 'utils/sentry';
import useTokenAllowance from 'hooks/useTokenAllowance'; // Adjust import path if needed
import { useTransactionAdder, useHasPendingApproval } from 'state/transactions/hooks';
import { calculateGasMargin } from 'utils';
import { useTokenContract } from 'hooks/useContract'; // Adjust import path if needed
import { useCallWithGasPrice } from 'hooks/useCallWithGasPrice';
import { testnetTokens, mainnetTokens } from 'config/constants/tokens';

export enum ApprovalState {
  UNKNOWN,
  NOT_APPROVED,
  PENDING,
  APPROVED,
}

export function useApproveUsdcCallback(
  amountToApprove?: string,
  spender?: string,
): [ApprovalState, () => Promise<void>] {
  const { account } = useActiveWeb3React();
  const { callWithGasPrice } = useCallWithGasPrice();
  const token = process.env.REACT_APP_PUBLIC_CHAIN_ID === '43113' ? testnetTokens.usdc : mainnetTokens.usdc; // Select USDC token
  const currentAllowance = useTokenAllowance(token, account ?? undefined, spender);
  const pendingApproval = useHasPendingApproval(token?.address, spender);

  // Determine approval state
  const approvalState: ApprovalState = useMemo(() => {
    if (!amountToApprove || !spender) return ApprovalState.UNKNOWN;
    if (!currentAllowance) return ApprovalState.UNKNOWN;

    return currentAllowance.lessThan(amountToApprove)
      ? pendingApproval
        ? ApprovalState.PENDING
        : ApprovalState.NOT_APPROVED
      : ApprovalState.APPROVED;
  }, [amountToApprove, currentAllowance, pendingApproval, spender]);

  const tokenContract = useTokenContract(token?.address);
  const addTransaction = useTransactionAdder();

  const approve = useCallback(async (): Promise<void> => {
    if (approvalState !== ApprovalState.NOT_APPROVED) {
      console.error('approve was called unnecessarily');
      return;
    }
    if (!token) {
      console.error('no token');
      return;
    }
    if (!tokenContract) {
      console.error('tokenContract is null');
      return;
    }
    if (!amountToApprove) {
      console.error('missing amount to approve');
      return;
    }
    if (!spender) {
      console.error('no spender');
      return;
    }

    let useExact = false;
    const estimatedGas = await tokenContract.estimateGas.approve(spender, MaxUint256).catch(() => {
      // Fallback for tokens with restricted approval amounts
      useExact = true;
      return tokenContract.estimateGas.approve(spender, amountToApprove);
    });

    return callWithGasPrice(
      tokenContract,
      'approve',
      [spender, useExact ? amountToApprove : MaxUint256],
      {
        gasLimit: calculateGasMargin(estimatedGas),
      },
    )
      .then((response: TransactionResponse) => {
        addTransaction(response, {
          summary: `Approve USDC`,
          approval: { tokenAddress: token.address, spender },
        });
      })
      .catch((error: Error) => {
        logError(error);
        console.error('Failed to approve token', error);
        throw error;
      });
  }, [approvalState, token, tokenContract, amountToApprove, spender, addTransaction, callWithGasPrice]);

  return [approvalState, approve];
}
